import { Component, computed, effect, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { type Database } from '@app/types/supabase';
import { ApiService } from '@app/core/api.service';
import { DefenseBdService } from '@app/core/defense-bd.service';
import { InboxService } from '@app/core/inbox.service';
import { type IWorkspaceSummary, SessionService } from '@app/core/session.service';
import { SupabaseService } from '@app/core/supabase.service';
import { TechVectorService } from '@app/core/tech-vector.service';
import { WorkspaceService } from '@app/core/workspace.service';
import { DbTable } from '@app/models/models.helpers';
import { SbImageComponent } from '@app/components/sb-image/sb-image.component';
import { IdenticonComponent } from '@app/components/identicon/identicon.component';

type ActivityFilter = 'all' | 'tv' | 'reports' | 'budget';

export type ActivityItem =
  | { kind: 'tv'; id: string; title: string; subtitle: string; date: string; workspaceId: string | null }
  | { kind: 'report'; id: string; title: string; subtitle: string; date: string; workspaceId: string; tvId: string }
  | { kind: 'budget'; id: string; title: string; subtitle: string; date: string };

@Component({
  selector: 'app-user-home2',
  imports: [
    DatePipe,
    FormsModule,
    ButtonModule,
    DialogModule,
    CheckboxModule,
    InputTextModule,
    SkeletonModule,
    SbImageComponent,
    IdenticonComponent,
  ],
  templateUrl: './user-home2.component.html',
  styleUrl: './user-home2.component.scss',
})
export class UserHome2Component {
  sessionService = inject(SessionService);
  workspaceService = inject(WorkspaceService);
  apiService = inject(ApiService);
  tvService = inject(TechVectorService);
  bdService = inject(DefenseBdService);
  inboxService = inject(InboxService);
  supabase = inject(SupabaseService);
  router = inject(Router);

  // ─── Left rail: org + workspaces ───
  workspacesForActiveOrganization = computed<IWorkspaceSummary[]>(() => {
    const activeOrgId = this.sessionService.activeOrganizationId();
    if (!activeOrgId) return [];
    return this.sessionService
      .workspaceOptions()
      .filter(ws => ws.organization_id === activeOrgId)
      .sort((a, b) => (b.last_accessed_at ?? '').localeCompare(a.last_accessed_at ?? ''));
  });

  // ─── Center rail: recent activity feed ───
  activeFilter = signal<ActivityFilter>('all');
  activityLoading = signal(true);

  private recentTechVectors = signal<DbTable<'tech_vectors'>[]>([]);
  private recentReports = signal<DbTable<'tv_reports'>[]>([]);
  private recentBudgetSearches = signal<DbTable<'budget_searches'>[]>([]);

  filteredActivity = computed<ActivityItem[]>(() => {
    const filter = this.activeFilter();

    const tvItems: ActivityItem[] = this.recentTechVectors().map(tv => ({
      kind: 'tv',
      id: tv.id,
      title: tv.title || 'Untitled Tech Vector',
      subtitle: this.getWorkspaceName(tv.workspace_id),
      date: tv.updated_at ?? tv.created_at ?? '',
      workspaceId: tv.workspace_id,
    }));

    const reportItems: ActivityItem[] = this.recentReports().map(r => ({
      kind: 'report',
      id: r.id,
      title: r.title || 'Untitled Report',
      subtitle: this.getWorkspaceName(r.workspace_id),
      date: r.updated_at ?? r.created_at ?? '',
      workspaceId: r.workspace_id,
      tvId: r.tv_id,
    }));

    const budgetItems: ActivityItem[] = this.recentBudgetSearches().map(b => ({
      kind: 'budget',
      id: b.id,
      title: b.name,
      subtitle: '',
      date: b.updated_at ?? b.created_at ?? '',
    }));

    if (filter === 'tv') return tvItems;
    if (filter === 'reports') return reportItems;
    if (filter === 'budget') return budgetItems;

    return [...tvItems, ...reportItems, ...budgetItems].sort((a, b) => b.date.localeCompare(a.date));
  });

  // ─── Right rail: notifications (inbox) ───
  inboxFilter = signal<'unread' | 'all'>('unread');
  inboxItems = computed<DbTable<'inbox_items'>[]>(() => {
    const items = [...this.inboxService.inbox()].sort(
      (a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''),
    );
    if (this.inboxFilter() === 'unread') return items.filter(i => i.status === 'UNREAD');
    return items;
  });
  numUnread = computed(() => this.inboxService.numUnread());

  // ─── Workspace picker (gates new-TV creation) ───
  workspacePickerVisible = signal(false);
  private pendingNewTv = signal(false);

  // ─── Create workspace dialog ───
  createWorkspaceDialogVisible = signal(false);
  createWorkspaceName = signal('');
  createWorkspaceIsDefault = signal(false);
  createWorkspaceLoading = signal(false);
  createWorkspaceError = signal<string | null>(null);

  constructor() {
    effect(() => {
      const userId = this.sessionService.userId();
      const activeOrgId = this.sessionService.activeOrganizationId();
      if (!userId || !activeOrgId) {
        this.recentTechVectors.set([]);
        this.recentReports.set([]);
        this.recentBudgetSearches.set([]);
        this.activityLoading.set(false);
        return;
      }
      void this.loadRecentActivity();
    });
    this.inboxService.loadInbox();
  }

  getWorkspaceName(workspaceId: string): string {
    return this.sessionService.workspaceOptions().find(ws => ws.id === workspaceId)?.name ?? 'Untitled Workspace';
  }

  // ─── Data loading ───
  private async loadRecentActivity() {
    this.activityLoading.set(true);
    await Promise.all([
      this.loadRecentTechVectors(),
      this.loadRecentReports(),
      this.loadRecentBudgetSearches(),
    ]);
    this.activityLoading.set(false);
  }

  private async loadRecentTechVectors() {
    const userId = this.sessionService.userId();
    const activeOrgId = this.sessionService.activeOrganizationId();
    if (!userId || !activeOrgId) { this.recentTechVectors.set([]); return; }
    const { data, error } = await this.supabase.client
      .from('tech_vectors')
      .select('*, workspaces!inner(organization_id)')
      .eq('creator_id', userId)
      .eq('workspaces.organization_id', activeOrgId)
      .in('status', ['COMPLETED', 'RUNNING'])
      .order('updated_at', { ascending: false })
      .limit(15);
    if (error) console.error('Error loading recent tech vectors', error);
    this.recentTechVectors.set(
      (data ?? []).map(({ workspaces: _ws, ...tv }) => tv as DbTable<'tech_vectors'>),
    );
  }

  private async loadRecentReports() {
    const userId = this.sessionService.userId();
    const activeOrgId = this.sessionService.activeOrganizationId();
    if (!userId || !activeOrgId) { this.recentReports.set([]); return; }
    const { data, error } = await this.supabase.client
      .from('tv_reports')
      .select('*, workspaces!inner(organization_id)')
      .eq('creator_id', userId)
      .eq('workspaces.organization_id', activeOrgId)
      .order('updated_at', { ascending: false })
      .limit(15);
    if (error) console.error('Error loading recent reports', error);
    this.recentReports.set(
      (data ?? []).map(({ workspaces: _ws, ...r }) => r as DbTable<'tv_reports'>),
    );
    console.log('recent reports', data);
  }

  private async loadRecentBudgetSearches() {
    const userId = this.sessionService.userId();
    if (!userId) { this.recentBudgetSearches.set([]); return; }
    const { data, error } = await this.supabase.client
      .from('budget_searches')
      .select('*')
      .eq('creator_id', userId)
      .order('updated_at', { ascending: false })
      .limit(15);
    if (error) console.error('Error loading recent budget searches', error);
    this.recentBudgetSearches.set(data ?? []);
  }

  // ─── Right rail: notifications (inbox) ───
  notificationIcon(item: DbTable<'inbox_items'>): string {
    switch (item.severity) {
      case 'SUCCESS': return 'pi pi-check-circle';
      case 'WARN': return 'pi pi-exclamation-triangle';
      case 'HELP': return 'pi pi-question-circle';
      case 'INFO': return 'pi pi-info-circle';
      default: return 'pi pi-bell';
    }
  }

  notificationIconClass(item: DbTable<'inbox_items'>): string {
    switch (item.severity) {
      case 'SUCCESS': return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500';
      case 'WARN': return 'bg-amber-50 dark:bg-amber-900/20 text-amber-500';
      case 'HELP': return 'bg-violet-50 dark:bg-violet-900/20 text-violet-500';
      case 'INFO': return 'bg-blue-50 dark:bg-blue-900/20 text-blue-500';
      default: return 'bg-orange-50 dark:bg-orange-900/20 text-[#F97002]';
    }
  }

  async onClickNotification(item: DbTable<'inbox_items'>) {
    await this.inboxService.handleClickAction(item, { markAsRead: true, markAsToasted: true });
  }

  markNotificationRead(event: Event, item: DbTable<'inbox_items'>) {
    event.stopPropagation();
    void this.inboxService.markAsRead(item);
  }

  archiveNotification(event: Event, item: DbTable<'inbox_items'>) {
    event.stopPropagation();
    void this.inboxService.archiveMessage(item);
  }

  markAllNotificationsRead() {
    void this.inboxService.markAllAsRead();
  }

  // ─── Navigation: left rail ───
  onSelectWorkspace(workspace: IWorkspaceSummary) {
    this.router.navigate(['/app', 'ws', workspace.id]);
  }

  navigateToOrgSettings() {
    const orgId = this.sessionService.activeOrganizationId();
    if (!orgId) return;
    this.router.navigate(['/app/organizations', orgId]);
  }

  navigateToOrganizations() {
    this.router.navigate(['/app/organizations']);
  }

  // ─── Navigation: activity feed ───
  /** Id of the activity item currently being opened (shows a spinner). */
  openingItemId = signal<string | null>(null);

  async onOpenActivityItem(item: ActivityItem) {
    console.log('opening item', item);
    if (this.openingItemId()) return;
    if (item.kind === 'tv') {
      const workspaceId = item.workspaceId ?? this.workspaceService.activeWorkspace()?.workspace_id;
      if (!workspaceId) return;
      this.router.navigate(['/app/ws', workspaceId, 'tv', item.id]);
    } else if (item.kind === 'report') {
      console.log('opening report', item);
      // return;
      if (!item.workspaceId || !item.tvId) return;
      // The reports route is gated by `tvModulesReadyGuard`, which inspects the
      // TV's modules/manifest. On a cold deep-link from here those aren't primed
      // yet (route guards run before the parent's resolver), so prime the TV
      // first to avoid being bounced to the dashboard.
      this.openingItemId.set(item.id);
      // try {
      //   await this.tvService.loadActiveTechVector(item.tvId);
      //   await this.router.navigate(['/app/ws', item.workspaceId, 'tv', item.tvId, 'reports', item.id]);
      // } finally {
      //   this.openingItemId.set(null);
      // }
    } else if (item.kind === 'budget') {
      const workspaceId = this.workspaceService.activeWorkspace()?.workspace_id
        ?? this.workspacesForActiveOrganization()[0]?.id;
      if (!workspaceId) return;
      await this.bdService.loadBudgetSearch(item.id);
      this.router.navigate(['/app/ws', workspaceId, 'bd', 'search', 'overview']);
    }
  }

  // ─── New Tech Vector flow (workspace-gated) ───

  /**
   * Top-level "New Tech Vector" CTA. Resolves which workspace the new tech
   * vector should live in:
   *  - no workspaces  → prompt the user to create one first
   *  - one workspace  → skip straight to the create flow
   *  - many           → ask the user to pick via the picker dialog
   */
  startNewTechVector() {
    const workspaces = this.workspacesForActiveOrganization();

    if (workspaces.length === 0) {
      this.pendingNewTv.set(true);
      this.openCreateWorkspaceDialog();
      return;
    }

    if (workspaces.length === 1) {
      this.navigateToNewTv(workspaces[0].id);
      return;
    }

    this.pendingNewTv.set(true);
    this.workspacePickerVisible.set(true);
  }

  onPickWorkspace(workspace: IWorkspaceSummary) {
    this.workspacePickerVisible.set(false);
    this.pendingNewTv.set(false);
    this.navigateToNewTv(workspace.id);
  }

  private navigateToNewTv(workspaceId: string) {
    this.router.navigate(['/app/ws', workspaceId, 'tv', 'new']);
  }

  // ─── Create workspace dialog ───
  openCreateWorkspaceDialog() {
    this.createWorkspaceError.set(null);
    this.createWorkspaceName.set('');
    this.createWorkspaceIsDefault.set(false);
    this.createWorkspaceDialogVisible.set(true);
  }

  async createWorkspace() {
    const name = this.createWorkspaceName().trim();
    if (!name || this.createWorkspaceLoading()) return;

    this.createWorkspaceLoading.set(true);
    this.createWorkspaceError.set(null);
    try {
      const newWorkspaceId: string = await this.apiService.apost<string>(
        `organizations/${this.sessionService.activeOrganizationId()}/create-workspace`,
        { name, is_default: this.createWorkspaceIsDefault() },
      );
      this.createWorkspaceDialogVisible.set(false);

      // If the user was mid-way through creating a tech vector, continue that
      // flow straight into the freshly created workspace.
      const continueToNewTv = this.pendingNewTv();
      this.pendingNewTv.set(false);
      this.sessionService.refreshSession();

      if (continueToNewTv) {
        this.navigateToNewTv(newWorkspaceId);
      } else {
        this.workspaceService.setActiveWorkspace(newWorkspaceId);
      }
    } catch (err: any) {
      console.error('Failed to create workspace', err);
      this.createWorkspaceError.set(
        err?.status === 403
          ? 'You are not authorized to create a workspace for this organization.'
          : 'Could not create workspace. Please try again.',
      );
    } finally {
      this.createWorkspaceLoading.set(false);
    }
  }
}
